/*
 * Author: zoesvision.com
 * Purpose: Manage accessible tab state, keyboard navigation, and Theme Editor selection.
 */

class ZTabs extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.triggers = [...this.querySelectorAll('[data-z-tab-trigger]')];
    this.navigation = this.querySelector('.z-tabs__navigation');
    this.panelsContainer = this.querySelector('.z-tabs__panels');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    this.triggers.forEach((trigger) => {
      if (!trigger.dataset.zTabFallback) trigger.dataset.zTabFallback = trigger.textContent.trim();
    });

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.activate(index), {
        signal: this.abortController.signal
      });
      trigger.addEventListener('keydown', (event) => this.onKeydown(event, index), {
        signal: this.abortController.signal
      });
    });

    document.addEventListener('shopify:block:select', this.onBlockSelect, {
      signal: this.abortController.signal
    });

    this.refreshPanels();
    this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
    if (this.navigation) this.resizeObserver.observe(this.navigation);

    if (this.panelsContainer) {
      this.mutationObserver = new MutationObserver(this.onPanelsMutation);
      this.mutationObserver.observe(this.panelsContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    this.dataset.zTabsReady = 'true';
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.abortController = null;
    this.mutationObserver = null;
    this.panelsContainer = null;
    this.navigation = null;
    this.triggers = [];
    this.panels = [];
  }

  refreshPanels() {
    // zoesvision.com | 2026-08-07 | Purpose: Refresh child block titles and panel references after Theme Editor rerenders.
    this.panels = [...this.querySelectorAll('[data-z-tab-block]')];
    const activeTrigger = this.triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true');
    const activeBlockId = activeTrigger?.dataset.zTabId || this.triggers[0]?.dataset.zTabId;

    this.triggers.forEach((trigger) => {
      const panel = this.panels.find((item) => item.dataset.zTabId === trigger.dataset.zTabId);
      const sourceTitle = panel?.querySelector('[data-z-tab-title-source]')?.textContent.trim();
      const title = sourceTitle || trigger.dataset.zTabFallback;

      if (title && trigger.textContent.trim() !== title) trigger.textContent = title;

      const fallbackTitle = panel?.querySelector('[data-z-tab-fallback-title]');
      if (fallbackTitle && title && fallbackTitle.textContent.trim() !== title) {
        fallbackTitle.textContent = title;
      }
    });

    const activeIndex = this.triggers.findIndex((trigger) => trigger.dataset.zTabId === activeBlockId);
    if (activeIndex >= 0) this.activate(activeIndex, false, false, false);
  }

  activate(index, focus = false, announce = true, scroll = true) {
    const trigger = this.triggers[index];
    if (!trigger) return;

    const blockId = trigger.dataset.zTabId;

    this.triggers.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.zTabId === blockId;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
      panel.inert = !active;

      if (active) {
        const fallbackTitle = panel.querySelector('[data-z-tab-fallback-title]');
        if (fallbackTitle && !fallbackTitle.textContent.trim()) {
          fallbackTitle.textContent = trigger.textContent.trim();
        }
      }
    });

    this.updateIndicator();

    if (focus) trigger.focus();
    if (scroll) {
      trigger.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    if (announce) {
      this.dispatchEvent(new CustomEvent('z:tabs:change', {
        bubbles: true,
        detail: { index, blockId }
      }));
    }
  }

  updateIndicator() {
    const activeTrigger = this.triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true');
    if (!this.navigation || !activeTrigger) return;

    this.navigation.style.setProperty('--z-tabs-indicator-x', `${activeTrigger.offsetLeft}px`);
    this.navigation.style.setProperty('--z-tabs-indicator-width', `${activeTrigger.offsetWidth}px`);
    if (this.navigation.dataset.zIndicatorReady !== 'true') {
      requestAnimationFrame(() => {
        if (this.navigation?.isConnected) this.navigation.dataset.zIndicatorReady = 'true';
      });
    }
  }

  onKeydown(event, index) {
    const lastIndex = this.triggers.length - 1;
    let targetIndex = null;

    if (event.key === 'ArrowRight') targetIndex = (index + 1) % this.triggers.length;
    if (event.key === 'ArrowLeft') targetIndex = (index - 1 + this.triggers.length) % this.triggers.length;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = lastIndex;
    if (targetIndex === null) return;

    event.preventDefault();
    this.activate(targetIndex, true);
  }

  onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const panel = event.target.closest('[data-z-tab-block]');
    if (!panel) return;

    const index = this.triggers.findIndex((trigger) => trigger.dataset.zTabId === panel.dataset.zTabId);
    if (index >= 0) this.activate(index);
  };

  onPanelsMutation = () => {
    if (this.refreshQueued) return;

    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      this.refreshPanels();
    });
  };
}

if (!customElements.get('z-tabs')) {
  customElements.define('z-tabs', ZTabs);
}
